import React from 'react';
import { QAOperationJson } from '@scale/shared/types/qaOperation/qaOperation';
import { useQaOperationStateMachine } from 'react-scale/hooks/qaOperation/useQaOperationStateMachine';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button } from '@mui/material';
import { useRemoAdminCtx } from '../remoAdminContext';
import { openOperationPopup } from './tables/helpers';
import { qaOperationHasEvent } from './qaOperationContext';

interface IViewOperationProps {
  onClose?: () => void;
  qaOperation: QAOperationJson;
  fullWidth?: boolean;
}

export const ViewOperationButton: React.FC<IViewOperationProps> = props => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const { qaOperation, onClose, fullWidth } = props;
  return (
    <Button
      variant="contained"
      fullWidth={fullWidth}
      onClick={() => openOperationPopup(qaOperation, isRemoAdmin, onClose)}
    >
      {` View `}
    </Button>
  );
};

interface IOperationActionProps {
  qaOperation: QAOperationJson;
  userId: string;
  attemptResult?: any;
  onSubmit?: () => void;
  disabled?: boolean;
}

export const CancelOperationButton: React.FC<IOperationActionProps> = ({
  userId,
  qaOperation,
  disabled,
  onSubmit,
}) => {
  const { transitionOperationResult, cancelOperation } = useQaOperationStateMachine({ userId });

  const handleCancel = React.useCallback(async () => {
    if (
      confirm(`Are you sure you want to cancel this operation?\nOperation ID: ${qaOperation._id}`)
    ) {
      try {
        await cancelOperation(qaOperation._id);
        if (onSubmit) onSubmit();
      } catch (error) {
        alert(`Failed to cancel operation ${qaOperation._id}: ${error}`);
      }
    }
  }, [qaOperation, cancelOperation, onSubmit]);

  return (
    <LoadingButton
      key="cancel-node"
      variant="contained"
      color="error"
      size="small"
      loading={transitionOperationResult.loading || disabled}
      onClick={handleCancel}
    >
      Cancel
    </LoadingButton>
  );
};

interface ISubmitOperationActionProps extends IOperationActionProps {
  reviewMessage: string;
}

export const QAOperationSubmitActions: React.FC<ISubmitOperationActionProps> = props => {
  const { onSubmit, reviewMessage, qaOperation, userId, attemptResult } = props;

  const {
    transitionOperationResult,
    completeAttemptNode,
    retryTransition,
    rejectNode,
    approveNode,
  } = useQaOperationStateMachine({
    userId,
  });

  const { loading } = transitionOperationResult;

  const canCompleteReview = qaOperationHasEvent('completeReview', qaOperation);
  const canCompleteAttempt = qaOperationHasEvent('completeAttempt', qaOperation);
  const canRetry = qaOperationHasEvent('retry', qaOperation);

  const transitionHandler = React.useCallback(
    (nodeOperation: (opID: string) => Promise<void>) => {
      return async () => {
        await nodeOperation(qaOperation._id);
        if (onSubmit) onSubmit();
      };
    },
    [onSubmit, qaOperation],
  );

  const handleReject = React.useMemo(
    () => transitionHandler((operationId: string) => rejectNode(operationId, reviewMessage)),
    [rejectNode, reviewMessage, transitionHandler],
  );

  const handleApprove = React.useMemo(
    () =>
      transitionHandler((operationId: string) =>
        approveNode(operationId, reviewMessage || 'approved'),
      ),
    [approveNode, reviewMessage, transitionHandler],
  );

  const handleCompleteAttempt = React.useMemo(
    () =>
      transitionHandler((operationId: string) => completeAttemptNode(operationId, attemptResult)),
    [completeAttemptNode, attemptResult, transitionHandler],
  );

  const handleRetry = React.useMemo(
    () => transitionHandler(retryTransition),
    [retryTransition, transitionHandler],
  );

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '1ex' }}>
      {canRetry && (
        <Button variant="contained" onClick={handleRetry} disabled={loading}>
          Retry Transition
        </Button>
      )}
      {canCompleteReview && (
        <>
          <Button variant="contained" onClick={handleApprove} disabled={loading}>
            Approve
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleReject}
            disabled={loading || !reviewMessage}
          >
            Submit Rejection {!reviewMessage ? '(Requires review message)' : ''}
          </Button>
        </>
      )}
      {canCompleteAttempt && (
        <Button variant="contained" onClick={handleCompleteAttempt} disabled={loading}>
          Submit
        </Button>
      )}
    </div>
  );
};
